import React, { useContext, useEffect, useState } from 'react';
import { useMount } from 'react-use';
import { useBlocker, useLocation, useNavigate } from 'react-router-dom';

import Checkbox from '@mui/material/Checkbox';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import { userApi, videoApi } from '../../api/openapi-axios-client';
import VideoPlayer from './VideoPlayer';
import { Button, TrustMeter } from '../../elements';
import { truncateString } from '../../helpers/strings';
import RootContext from '../../services/context-states/root-context';
import { Video, RatingOption } from '../../types/videos-page';
import { NextSurveyResponse } from '../../types/api-client-types';
import { callApi, isNotFoundError } from '../../api/helpers';

const VideosPage: React.FC = () => {
    const { appTourContext, userContext } = useContext(RootContext);
    const { currentUser } = userContext;
    const { appTour, setTourState } = appTourContext;
    const [video, setVideo] = useState<Video>({} as Video);
    const [videoEndTimestamp, setVideoEndTimestamp] = useState<Date | null>(null);
    const [rating, setRating] = useState<number>(0);
    const [watchCount, setWatchCount] = useState<number>(-1);
    const [showProgessPopUp, setShowProgessPopUp] = useState<boolean>(false);
    const [showMore, setShowMore] = useState<boolean>(false);
    const [mobileTab, setMobileTab] = useState<number>(0);

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const ratingOptions: RatingOption[] = [
        { value: 1, label: 'Not Interesting' },
        { value: 2, label: 'Slighly Interesting' },
        { value: 3, label: 'Somewhat Interesting' },
        { value: 4, label: 'Quite Interesting' },
        { value: 5, label: 'Very Interesting' },
    ];

    const fullTrustAmount = 32;
    const descriptionMaxLength = 50;

    useEffect(() => {
        if (watchCount === -1 && currentUser?.id) {
            callApi(() => videoApi.watchLog.getHistory('count', currentUser.id))
                .then(data => {;
                    setWatchCount(Number(data));
                })
                .catch((error) => {
                    console.log('error', error);
                });

            fetchNextVideo();
        }
    }, [currentUser]);

    useMount(() => {
        if (appTour.tourActive) {
            setTimeout(() => {
                setTourState((prevState) => ({ 
                    ...prevState, 
                    run: true
                }));
            }, 500);
        }
    });

    useBlocker(() => {
        callApi(() => videoApi.watchLog.updateVideoLog({
            rating: 0,
            ended: videoEndTimestamp?.toISOString() || new Date().toISOString(),
            watchId: video.watchId
        }));

        return false;
    });

    const fetchNextVideo = async (): Promise<void> => {
        try {
            const data = await callApi<Video>(() => videoApi.videos.getNextVideo());
            await setupViewingHistory(data);
            setVideo(data);
        } catch (error) {
            console.error('Error fetching next video:', error);
        }
    };

    const setupViewingHistory = async (data: Video): Promise<void> => {
        try {
            await callApi(() => videoApi.watchLog.addVideoLog({
                watchId: data.watchId,
                source: 'youtube',
                sourceId: data.id,
                url: data.url
            }));
            setRating(0);
        } catch (error) {
            console.error('Error setting up viewing history:', error);
        }
    };

    const submitVideoRating = async (): Promise<void> => {
        await callApi(() => videoApi.watchLog.updateVideoLog({
            rating,
            ended: videoEndTimestamp?.toISOString() || new Date().toISOString(),
            watchId: video.watchId
        }));

        const newWatchCount = watchCount + 1;

        if (newWatchCount === Number(fullTrustAmount / 2) || newWatchCount === fullTrustAmount) {
            setShowProgessPopUp(true);
        }

        setWatchCount(newWatchCount);

        setVideoEndTimestamp(null);
        setRating(0);
        setShowMore(false);

        try {
            // pendingVideo=true tells the server to count the video just rated above,
            // whose watch-log write is still in flight on the queue, so the survey
            // threshold fires on this rating instead of one video late.
            const nextSurvey = await callApi<NextSurveyResponse>(() => userApi.userSurveys.getNextSurvey({ params: { pendingVideo: true } }));
            if (nextSurvey?.surveyKey) {
                navigate(`/survey/${nextSurvey.surveyKey}?returnTo=${encodeURIComponent(location.pathname)}`);
                return;
            }
        } catch (error) {
            // A 404 means there is no survey pending — not an error, just continue.
            if (!isNotFoundError(error)) {
                console.error('Error fetching next survey:', error);
            }
        }

        fetchNextVideo();
    };

    const endOfVideoEvent = (): void => {
        setVideoEndTimestamp(new Date());
    };

    const rateVideo = (newRating: number): void => setRating(newRating);

    const getProgressPopUpContent = () => {
        
        if(watchCount === Number(fullTrustAmount / 2)) {
            return (
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 20}}>
                    <Typography sx={{fontSize: 32}}>{String.fromCodePoint(0x1F389)}</Typography>
                    <Typography sx={{fontSize: 16, textAlign: 'center'}}>
                        Congrats! You're halfway to getting your Results!
                    </Typography>
                    <Button format='primary' text='Continue watching' style={{width: 150, fontSize: 10, marginTop: 2}} onClick={() => {setShowProgessPopUp(false)}} aria-label="Continue watching videos" />
                </div>
            );
        }
        else if (watchCount === fullTrustAmount) {
            return (
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 20}}>
                    <Typography sx={{fontSize: 32}}>{String.fromCodePoint(0x02705)}</Typography>
                    <Typography sx={{fontSize: 16, textAlign: 'center'}}>
                        Congrats! You've finished your first set of videos!
                    </Typography>
                    <div style={{display: 'flex', flexDirection: 'row', gap: 2, marginTop: 2}}>
                        <Button format='text' text='Watch more' style={{width: 105, fontSize: 10}} onClick={() => {setShowProgessPopUp(false)}} aria-label="Watch more videos" />
                        <Button format='primary' text='View results' style={{width: 105, fontSize: 10}} onClick={() => {setShowProgessPopUp(false); navigate('/results')}} aria-label="View your results" />
                    </div>
                </div>
            );
        }
        else {
            return null;
        }
    }

    // Mobile Layout
    if (isMobile) {
        return (
            <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 }, pb: 10 }}>
                <Typography sx={{
                    fontSize: { xs: 18, sm: 24 },
                    fontWeight: 'bold',
                    color: 'text.primary',
                    mb: { xs: 1.5, sm: 2 },
                    lineHeight: 1.2
                }}>
                    {truncateString(video.title, isSmallMobile ? 60 : 80)}
                </Typography>

                <Box sx={{
                    width: '100%',
                    aspectRatio: '16/9',
                    mb: 2,
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: 2
                }}>
                    <VideoPlayer
                        video={video}
                        autoplay={1}
                        onEndFunc={endOfVideoEvent}
                        addtionalOptions={{}}
                        additionalVars={{}}
                        height={'100%'}
                        width={'100%'}
                    />
                </Box>

                <TrustMeter
                    fullAmount={fullTrustAmount}
                    trustAmount={watchCount}
                    style={{
                        height: isSmallMobile ? 44 : 50,
                        marginBottom: isSmallMobile ? 12 : 16,
                        borderRadius: 8
                    }}
                />

                <Tabs
                    value={mobileTab}
                    onChange={(_e, newValue) => setMobileTab(newValue)}
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        mb: { xs: 2, sm: 3 },
                        '& .MuiTab-root': {
                            fontSize: { xs: 13, sm: 16 },
                            fontWeight: 600,
                            minHeight: { xs: 44, sm: 48 },
                            textTransform: 'none'
                        },
                        '& .MuiTabs-indicator': {
                            height: 3
                        }
                    }}
                    variant="fullWidth"
                >
                    <Tab label="Rate This Career" />
                    <Tab label="Details" />
                </Tabs>

                <Box sx={{ mb: 10, px: { xs: 0, sm: 1 } }}>
                {mobileTab === 0 && (
                        <Box className='tour__shareYourFeedback'>
                            <Typography sx={{ fontSize: { xs: 15, sm: 16 }, fontWeight: 900, color: 'text.primary', mb: { xs: 1.5, sm: 2 } }}>
                                Did you find this career interesting?
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.5, sm: 1 } }}>
                                {ratingOptions.map(option => (
                                    <Box key={option.value} sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        justifyContent: 'flex-start',
                                        py: { xs: 0.5, sm: 0 },
                                        cursor: 'pointer',
                                        borderRadius: 1,
                                        px: { xs: 0.5, sm: 0 },
                                        '&:hover': {
                                            backgroundColor: 'action.hover'
                                        }
                                    }} onClick={() => { rating === option.value ? rateVideo(0) : rateVideo(option.value) }}>
                                        <Checkbox
                                            icon={<RadioButtonUncheckedIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />}
                                            checkedIcon={<CheckCircleIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />}
                                            checked={rating === option.value}
                                            onChange={() => { rating === option.value ? rateVideo(0) : rateVideo(option.value)}}
                                            sx={{ '&.Mui-checked': { color: 'primary' }, p: { xs: 0.5, sm: 1 } }}/>
                                        <Typography sx={{ fontSize: { xs: 14, sm: 15 }, color: 'text.primary', cursor: 'pointer' }}>
                                            {option.value} - {option.label}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}
                    {mobileTab === 1 && (
                        <Box className='tour__learnAboutEachCareer'>
                            <Typography sx={{ fontSize: { xs: 14, sm: 15 }, fontWeight: 'bold', color: 'text.primary', mb: 0.5 }}>
                                Careers shown in this video:
                            </Typography>
                            <Typography sx={{ fontSize: { xs: 15, sm: 16 }, color: 'text.primary', marginBottom: { xs: 1.5, sm: 2 } }}>
                                {video.onetData?.title}
                            </Typography>
                            <Typography sx={{ fontSize: { xs: 14, sm: 15 }, fontWeight: 'bold', color: 'text.primary', mb: 0.5 }}>
                                Career type:
                            </Typography>
                            <Typography sx={{ fontSize: { xs: 15, sm: 16 }, color: 'text.primary', marginBottom: { xs: 1.5, sm: 2 } }}>
                                {video.careerCluster?.title}
                            </Typography>
                            <Typography sx={{ fontSize: { xs: 14, sm: 15 }, fontWeight: 'bold', color: 'text.primary', mb: 0.5 }}>
                                Job Description:
                            </Typography>
                            <Typography sx={{ fontSize: { xs: 15, sm: 16 }, color: 'text.primary', lineHeight: 1.5 }}>
                                {showMore ? video.onetData?.description : truncateString(video.onetData?.description, descriptionMaxLength)}
                            </Typography>
                            {video.onetData?.description && video.onetData.description.length > descriptionMaxLength && (
                                <Button format='tertiary' style={{ color: 'primary', marginTop: 8 }} text={showMore ? 'Show Less' : 'Show More'} onClick={() => setShowMore(!showMore)} aria-label={showMore ? 'Show less content' : 'Show more content'} />
                            )}
                        </Box>
                    )}
                </Box>

                <Box sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: { xs: 2, sm: 3 },
                    backgroundColor: 'background.paper',
                    borderTop: 2,
                    borderColor: 'divider',
                    boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000,
                }}>
                    <Button
                        className='tour__keepWatchingVideos'
                        disabled={rating === 0}
                        format='primary'
                        text='Next Video'
                        style={{
                            width: '100%',
                            height: isSmallMobile ? 48 : 56,
                            borderRadius: 8,
                            fontSize: isSmallMobile ? 16 : 18,
                            fontWeight: 600,
                        }}
                        sx={{
                            '&.Mui-disabled': {
                                background: 'primary',
                                color: '#FFFFFF',
                                opacity: 0.5
                            }
                        }}
                        onClick={submitVideoRating}
                    />
                </Box>

                <Dialog open={showProgessPopUp} onClose={() => setShowProgessPopUp(false)} fullScreen={isSmallMobile}>
                    {getProgressPopUpContent()}
                </Dialog>
            </Container>
        );
    }

    // Desktop Layout
    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '20px', justifyContent: 'flex-start', alignItems: 'stretch', height: { md: 650, lg: 800 }, minHeight: 650, maxHeight: 800 }}>
                {/*VIDEO PLAYER*/}
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '70%' }}>
                    <Typography sx={{ fontSize: { md: 24, lg: 28 }, fontWeight: 'bold', color: 'text.primary' }}>
                        {truncateString(video.title, 100)}
                    </Typography>
                    <VideoPlayer
                        video={video}
                        autoplay={1}
                        onEndFunc={endOfVideoEvent}
                        addtionalOptions={{}}
                        additionalVars={{}}
                        height={'100%'}
                        width={'100%'}
                    />
                </Box>

                {/*VIDEO DETAILS*/}
                <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', width: '30%', borderRadius: '10px', borderColor: 'container.border', backgroundColor: 'container.background', borderWidth: '1px', borderStyle: 'solid', minWidth: { md: 280, lg: 300 } }}>
                <Dialog open={showProgessPopUp} onClose={() => setShowProgessPopUp(false)} disablePortal style={{position: 'absolute'}} slotProps={{backdrop: {style: {position: 'absolute'}}}}>
                    {getProgressPopUpContent()}
                </Dialog>
                <TrustMeter fullAmount={fullTrustAmount} trustAmount={watchCount} style={{ height: 50, borderTopLeftRadius: '10px', borderTopRightRadius: '10px' }} />
                    <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, padding: '15px' }}>           
                        <div className='tour__learnAboutEachCareer'>
                            <Typography sx={{ fontSize: 15, fontWeight: 'bold', color: 'text.primary' }}>
                                Careers shown in this video:
                            </Typography>
                            <Typography sx={{ fontSize: 16, color: 'text.primary', marginBottom: 2 }}>
                                {video.onetData?.title}
                            </Typography>
                            <Typography sx={{ fontSize: 15, fontWeight: 'bold', color: 'text.primary' }}>
                                Career type:
                            </Typography>
                            <Typography sx={{ fontSize: 16, color: 'text.primary', marginBottom: 2 }}>
                                {video.careerCluster?.title}
                            </Typography>
                            <Typography sx={{ fontSize: 15, fontWeight: 'bold', color: 'text.primary' }}>
                                Job Description:
                            </Typography>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxHeight: 160, overflowY: showMore ? 'scroll' : 'auto' }}>
                                <Typography sx={{ fontSize: 16, color: 'text.primary' }}>
                                    {showMore ? video.onetData?.description : truncateString(video.onetData?.description, descriptionMaxLength)}
                                </Typography>
                                {video.onetData?.description && video.onetData.description.length > descriptionMaxLength && (
                                    <Button format='tertiary' style={{ color: 'primary' }} text={showMore ? 'Show Less' : 'Show More'} onClick={() => setShowMore(!showMore)} aria-label={showMore ? 'Show less content' : 'Show more content'} />
                                )}
                            </div>
                        </div>
                        <Divider style={{ marginTop: 20, marginBottom: 20, color: 'divider' }} />
                        <div className='tour__shareYourFeedback'>
                            <Typography sx={{ fontSize: 16, fontWeight: 900, color: 'text.primary' }}>
                                Did you find this career interesting?
                            </Typography>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {ratingOptions.map(option => (
                                    <div key={option.value} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start' }}>
                                        <Checkbox
                                            icon={<RadioButtonUncheckedIcon />}
                                            checkedIcon={<CheckCircleIcon />}
                                            checked={rating === option.value}
                                            onChange={() => { rating === option.value ? rateVideo(0) : rateVideo(option.value)}}
                                            sx={{ '&.Mui-checked': { color: 'primary' }}}/>
                                        <Typography sx={{ fontSize: 14, color: 'text.primary', cursor: 'pointer' }} onClick={() => { rating === option.value ? rateVideo(0) : rateVideo(option.value) }}>
                                            {option.value} - {option.label}
                                        </Typography>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <Divider style={{ marginTop: 20, marginBottom: 20, color: 'divider' }} />
                        <Button
                            className='tour__keepWatchingVideos'
                            disabled={rating === 0}
                            format='primary'
                            text='Next Video'
                            style={{ height: 50, borderRadius: 2 }}
                            sx={{ '&.Mui-disabled': { background: 'primary', color: '#FFFFFF' } }}
                            onClick={submitVideoRating}
                        />
                    </div>
                </Box>
            </Box>
        </Container>
    );
};

export default VideosPage; 